import axios from "axios";
import API from "../http-common";

import { msalInstance, callApiToLogin, callApiToLogout } from "../utils/authUtils";

import { msalConfig } from "../authConfig";
import { PublicClientApplication } from "@azure/msal-browser";

// Redux-based session initialization
import { setLoginUser, setCurrUser, setIP, clearSession } from "../slices/sessionSlice";
import { hydrateConfigs, resetConfigs, setLod } from "src/slices/configsSlice";
import store from "../providers/store";

import { debugLog, errorLog } from "./debugUtils";

// /******** Environment Variables/Configurations ********/
const devTeam = ["yle@metrea.aero", "yle@msi.metrea.aero", "yle@msim.metrea.aero"];
const LOGOUT_IN_PROGRESS_KEY = "__logoutInProgress";

// Central helper to safely read session state
// Defensive fallback for early store initialization
const getReduxSession = () => {
	try {
		const state = store.getState();
		return state?.session || { currUser: {}, loginUser: {}, ip: "" };
	} catch {
		return { currUser: {}, loginUser: {}, ip: "" };
	}
};

// Backward-compatible accessor retained while call sites are migrated to direct Redux selectors.
export const Session = () => getReduxSession();
export const isLogoutInProgress = () => localStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === "1";

// ONLY if you want to debug session initialization
// debugLog(4, "[sessionUtils] Session accessor", { session: Session() });

/**
 * Initialize default sessionStorage values for loginUser and currUser.
 * If already set, loads from sessionStorage.
 */
export const initializeSessionUsers = () => {
	if (localStorage.getItem("__loggedOut") === "1") {
		debugLog(2, "[sessionUtils][FLOW] skipping rehydration after logout", {});
		return;
	}

	const sessionState = store.getState().session;

	// Skip if already hydrated
	if (sessionState.loginUser?.id && sessionState.currUser?.id) return;

	try {
		// Read persisted data from sessionStorage (if available)
		const persistedLogin = JSON.parse(sessionStorage.getItem("loginUser") || "{}");
		const persistedIP = JSON.parse(sessionStorage.getItem("ip") || '""');

		// Define default guest profile
		const guestUser = {
			homeAccountId: "",
			environment: "",
			tenantId: "",
			username: "",
			localAccountId: "",
			name: "Guest",
			idTokenClaims: {},
			email: "",
			full_name: "Guest",
			roles: "",
			mrg_id: "",
			lob_id: "",
			status_id: "",
			last_activity_time: "",
			createdAt: "",
			updatedAt: "",
			comments: "",
			internal_notes: "",
			createdBy: "",
			updatedBy: "",
			organization: "",
			job_title: "",
			duty_location: "",
			session_id: "",
		};

		const loginUser = Object.keys(persistedLogin).length ? persistedLogin : guestUser;

		// check if currUser already exists in Redux (hydrated from store.js) ---
		const postInitState = store.getState().session;
		const existingCurr = postInitState?.currUser || {};
		const shouldSkipReset = existingCurr?.id && loginUser?.id && existingCurr.id === loginUser.id;

		if (shouldSkipReset) {
			debugLog(3, "[sessionUtils][STATE] hydrated currUser detected", {
				currUser: existingCurr,
				loginUser,
			});
			// still ensure IP is restored
			store.dispatch(setIP(persistedIP));
			return;
		}

		// Dispatch to Redux instead of writing to sessionStorage
		const dispatch = store.dispatch;
		dispatch(setLoginUser(loginUser));
		// Only set currUser when not already hydrated
		dispatch(setCurrUser(loginUser));
		dispatch(setIP(persistedIP));

		debugLog(3, "[sessionUtils][STATE] initializeSessionUsers via Redux", {
			loginUser: loginUser.email || "Guest",
			ip: persistedIP || "n/a",
		});
	} catch (err) {
		errorLog(2, "[sessionUtils][FLOW] initializeSessionUsers failed", { error: err.message });
	}

	// Defensive re-sync if currUser is missing after reload
	try {
		const state = store.getState().session;
		if (!state.currUser?.id && state.loginUser?.id) {
			store.dispatch(setCurrUser(state.loginUser));
			debugLog(3, "[sessionUtils][STATE] rehydrated currUser from loginUser fallback", {
				user: state.loginUser.email,
			});
		}
	} catch (err) {
		errorLog(2, "[sessionUtils][FLOW] rehydrate currUser failed", { error: err.message });
	}
};

/**
 * Checks if the user is logged in.
 * @returns {boolean} True if logged in.
 */
export const isUserLogin = () => {
	// Hard logout barrier — do not allow implicit re-login
	if (localStorage.getItem("__loggedOut") === "1") {
		return false;
	}

	const { currUser } = getReduxSession();
	const loggedIn = !!currUser && !!currUser.id && currUser.id > 0;
	return loggedIn;
};

/**
 * Checks if the user (or currUser by default) has DEV role.
 * @param {object} [user] - User object to check.
 * @returns {boolean}
 */
export const isDeveloper = (user) => {
	const { currUser } = getReduxSession();
	const u = user || currUser;
	const result = Array.isArray(u?.user_roles)
		? u.user_roles.some((r) => r.role_code?.toUpperCase().includes("DEV"))
		: false;

	return result;
};

/**
 * Checks if the loginUser or currUser is in the devTeam email list.
 * @param {'login'|'acting'} [checking='login']
 * @returns {boolean}
 */
export const isDevTeam = (checking = "login") => {
	const { currUser, loginUser } = getReduxSession();
	const user = checking.toLowerCase() === "acting" ? currUser || {} : loginUser || {};

	// ID=1 fallback preserves historical admin/dev behavior even if email aliases change.
	const result =
		(!!user &&
			Array.isArray(devTeam) &&
			devTeam.some((email) => email.toLowerCase() === (user.email || "").toLowerCase())) ||
		loginUser?.id === 1;

	return result;
};

/**
 * Checks if the user(or currUser) has ADMIN role.
 * @param {object} [user] - User object to check.
 * @returns {boolean}
 */
export const isAdmin = (user) => {
	const { currUser } = getReduxSession();
	const u = user || currUser;
	const result = Array.isArray(u?.user_roles)
		? u.user_roles.some((r) => r.role_code?.toUpperCase().includes("ADMIN"))
		: false;
	// debugLog(4, "[sessionUtils] isAdmin", { result, roles: currUser?.user_roles });
	return result;
};

/**
 * Checks if the user (or currUser) has DSR (designer) role.
 * @param {object} [user] - User object to check.
 * @returns {boolean}
 */
export const isDesigner = (user) => {
	const { currUser } = getReduxSession();
	const u = user || currUser;
	const result = Array.isArray(u?.user_roles)
		? u.user_roles.some((r) => r.role_code?.toUpperCase().includes("DSR"))
		: false;

	return result;
};

/**
 * Checks if the user (default: currUser) has STRAT role.
 * @param {object} [user] - User object to check.
 * @returns {boolean}
 */
export const isStrat = (user) => {
	const { currUser } = getReduxSession();
	const u = user || currUser;
	const result = Array.isArray(u?.user_roles)
		? u.user_roles.some((r) => r.role_code?.toUpperCase().includes("STRAT"))
		: false;

	return result;
};

/**
 * Returns true if the userId is the owner of item.
 * @param {object} item - Target item.
 * @param {number|string} [userId] - User ID to check; defaults to currUser.
 * @returns {boolean}
 */
export const isOwner = (item, userId) => {
	// Redux-based owner check
	const { currUser } = getReduxSession();
	const id = userId ?? currUser.id;
	return !!item && item.createdBy === id;
};

/**
 * Returns a comma-separated list of role names for the user (default: currUser).
 * @param {object} [user] - User object to check.
 * @returns {string}
 */
export const getRoleNames = (user) => {
	// Redux-based role name list
	const { currUser } = getReduxSession();
	const u = user || currUser;
	return Array.isArray(u?.user_roles) && u.user_roles.length > 0
		? u.user_roles.map((r) => r.name).join(", ")
		: "Basic User";
};

/**
 * Returns a comma-separated list of role codes for the user (default: currUser).
 * @param {object} [user] - User object to check.
 * @returns {string}
 */
export const getRoleCodes = (user) => {
	// Redux-based role code list
	const { currUser } = getReduxSession();
	const u = user || currUser;
	return Array.isArray(u?.user_roles) && u.user_roles.length > 0
		? u.user_roles.map((r) => r.role_code).join(", ")
		: "";
};

/**
 * Checks if userId (default: currUser.id) is in group's user_accesses.
 * @param {object} group - Group object.
 * @param {number|string} [userId] - User ID to check; defaults to currUser.
 * @returns {boolean}
 */
export const isUserInGroup = (group, userId) => {
	// Redux-based group membership check
	const { currUser } = getReduxSession();
	const id = userId ?? currUser.id;
	if (!group || !Array.isArray(group.user_accesses)) return false;
	return group.user_accesses.some((access) => access.comp_value === id);
};

// Load user profile based on the email accordingly  6/23/2023
/**
 * Loads a user profile by email and sets it in session.
 * @param {string} email
 * @param {string} [target="currUser"] - "currUser", "loginUser", or "both"
 * @param {string|null} [redirect=null]
 * @param {string} [trigger="login"]
 * @returns {Promise<{ok: boolean, userProfile: object|null, redirectTarget: string|null}>}
 */
export async function loadUserProfile(email, target = "currUser", redirect = null, trigger = "login") {
	const redirectTarget =
		redirect === "reload"
			? "reload"
			: redirect
				? `${process.env.REACT_APP_URI}/${redirect}`
				: null;

	try {
		let response;
		// Identity switch operations must go through server-controlled auth endpoints.
		if (trigger === "actingAs") {
			response = await API.post("/auth/acting-as", { email });
		} else if (trigger === "switchBack") {
			response = await API.post("/auth/switch-back");
		} else {
			const action = trigger === "login" && redirect !== "reload" ? "login" : "find-by-email";
			// Await make wait until that
			// promise settles and return its result
			response = await API.get(`/users/${action}/${encodeURIComponent(email)}`);
		}
		const { data, headers } = response;

		// Guard: if the server ever sent HTML, bail out loudly
		if (headers?.["content-type"]?.includes("text/html") || typeof data !== "object") {
			throw new Error(
				"Expected JSON for user profile but received HTML/text. Check API base and API key.",
			);
		}

		// Trigger model:
		// - login: GET /api/users/login/:email
		// - actingAs: POST /api/auth/acting-as
		// - switchBack: POST /api/auth/switch-back
		// - other reads: GET /api/users/find-by-email/:email

		let userProfile = {};
		// Determine whether user exists by checking for a valid `id`
		// The new API returns `{}` when user is NOT found (truthy but empty),
		// so using `if (data)` is no longer valid.
		if (data && typeof data === "object" && data.id) {
			// existing user found
			const roles = (data.user_accesses || []).map((x) => x.user_role);
			userProfile = { ...data, user_roles: roles };
		} else {
			// Server-side login path auto-provisions users now.
			// Client should not attempt privileged /users creation fallback.
			throw new Error("User profile not found.");
		}

		debugLog(3, "[sessionUtils][STATE] loadUserProfile fetched", {
			email: email,
			target: target,
			userProfile: userProfile,
		});

		const storeUpdateOk = await setUsersInStore(userProfile, target);
		if (!storeUpdateOk) {
			throw new Error("Failed to update store state with loaded user profile.");
		}

		// Hydrate Redux configs from persisted sessionStorage after login
		try {
			const persistedConfigs = JSON.parse(sessionStorage.getItem("configs") || "{}");
			store.dispatch(hydrateConfigs(persistedConfigs));

			debugLog(3, "[sessionUtils][STATE] configs hydrated after login", {
				lod: persistedConfigs?.lod,
				persistedConfigs: persistedConfigs,
			});
		} catch (err) {
			errorLog(2, "[sessionUtils][FLOW] configs hydration failed", {
				error: err.message,
			});
		}

		// --- DEV ONLY / legacy reference ---
		// Dev-team default debug level
		if (isDevTeam()) {
			try {
				const { lod } = store.getState().configs;
				if (isDevTeam() && (lod === undefined || lod === 1)) {
					store.dispatch(setLod(9));
					debugLog(3, "[sessionUtils][STATE] dev team default LOD applied", {
						lod: 9,
					});
				}
			} catch (err) {
				errorLog(2, "[sessionUtils][FLOW] apply dev team default LOD failed", {
					error: err.message,
				});
			}
		}

		// Fetch public IP (Redux-based storage)
		try {
			const { data } = await axios.get("https://api.ipify.org/?format=json");
			store.dispatch(setIP(data.ip));
			debugLog(3, "[sessionUtils][STATE] loadUserProfile IP updated", { ip: data.ip });
		} catch (ipErr) {
			errorLog(2, "[sessionUtils][FLOW] loadUserProfile IP fetch failed", { error: ipErr.message });
		}

		return { ok: true, userProfile, redirectTarget };
	} catch (error) {
		errorLog(2, "[sessionUtils][FLOW] loadUserProfile failed", { error: error.message });
		const responseData = error?.response?.data;
		const message =
			responseData?.error === "acting_as_exit_only" &&
			typeof responseData.message === "string"
				? responseData.message
				: null;
		return { ok: false, userProfile: null, redirectTarget: null, message };
	}
}

export const applyRedirectTarget = (redirectTarget) => {
	if (redirectTarget === "reload") {
		window.location.reload();
		return true;
	}
	if (redirectTarget) {
		window.location.replace(redirectTarget);
		return true;
	}
	return false;
};

/**
 * Updates user data in sessionStorage for loginUser/currUser/both.
 * @param {Object} data - User object.
 * @param {string} [target="currUser"]
 */
/*
	async function setUsersInSessionLegacy(data = null, target = "currUser") {
		if (!data) return false;
		if (target === "both") {
			sessionStorage.setItem("loginUser", JSON.stringify(Object.assign(Session().loginUser, data)));
			sessionStorage.setItem("currUser", JSON.stringify(Object.assign(Session().currUser, data)));
			// window.location.replace(process.env.REACT_APP_URI + "/home");
		} else if (target === "loginUser") {
			sessionStorage.setItem("loginUser", JSON.stringify(Object.assign(Session().loginUser, data)));
			// window.location.replace(process.env.REACT_APP_URI);
		} else {
			sessionStorage.setItem("currUser", JSON.stringify(Object.assign(Session().currUser, data)));
			// window.location.reload();
		}

		debugLog(3, "[sessionUtils][STATE] setUsersInSessionLegacy", { data: data, target: target });
	}
*/
/**
 * Updates user data with Redux for loginUser/currUser/both.
 * @param {Object} data - User object.
 * @param {string} [target="currUser"]
 */
async function setUsersInStore(data = null, target = "currUser") {
	if (!data) return false;

	try {
		const dispatch = store.dispatch;

		if (target === "both") {
			dispatch(setLoginUser(data));
			dispatch(setCurrUser(data));
		} else if (target === "loginUser") {
			dispatch(setLoginUser(data));
		} else {
			dispatch(setCurrUser(data));
		}

		debugLog(3, "[sessionUtils][STATE] setUsersInStore via Redux", { target, data });

		return true;
	} catch (e) {
		errorLog(2, "[sessionUtils][FLOW] setUsersInStore failed", { error: e.message });
		return false;
	}
}

/********************************/
// Session Timeout settings

/**
 * Returns session timeout config based on environment.
 * @returns {Object}
 */
export const sessionConfig = () => {
	// Default policy when backend policy is unavailable: 60-minute idle + 60-second warning.
	const defaultIdleMs = 60 * 60 * 1000;
	const defaultWarningLeadMs = 60 * 1000;
	const raw = sessionStorage.getItem("sessionPolicy");
	if (raw) {
		try {
			const parsed = JSON.parse(raw);
			const idleTimeoutMs = Number(parsed?.idleTimeoutMs) || defaultIdleMs;
			const warningLeadMs = Number(parsed?.warningLeadMs) || defaultWarningLeadMs;
			// warningTime = idle duration before modal; redirectTime = modal grace window.
			return {
				warningTime: Math.max(1000, idleTimeoutMs - warningLeadMs),
				redirectTime: warningLeadMs,
			};
		} catch (_e) {
			// fallback to defaults below
		}
	}

	return {
		warningTime: defaultIdleMs - defaultWarningLeadMs,
		redirectTime: defaultWarningLeadMs,
	};
};

export const loadSessionPolicy = async () => {
	try {
		const { data } = await API.get("/session-policy");
		const idleTimeoutMs = Number(data?.idleTimeoutMs);
		const warningLeadMs = Number(data?.warningLeadMs);
		if (!Number.isFinite(idleTimeoutMs) || idleTimeoutMs <= 1000) return false;
		// Keep warning window valid even if backend/client values drift.
		const boundedWarningLeadMs =
			Number.isFinite(warningLeadMs) && warningLeadMs > 0
				? Math.min(warningLeadMs, idleTimeoutMs - 1000)
				: 60 * 1000;
		sessionStorage.setItem(
			"sessionPolicy",
			JSON.stringify({
				idleTimeoutMs,
				warningLeadMs: boundedWarningLeadMs,
			})
		);
		return true;
	} catch (error) {
		errorLog(2, "[sessionUtils][FLOW] loadSessionPolicy failed", { error: error?.message });
		return false;
	}
};

/******** Login/Logout ********/

/**
 * Handles backend login and redirects.
 *
 * This method sends a request to the back-end to sign in the use and if it's successful, it will send back SPA auth code to the front-end
 */
export const handleLogin = () => {
	localStorage.removeItem("__loggedOut");
	localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
	debugLog(2, "[sessionUtils][FLOW] handleLogin", {});
	callApiToLogin(); // Navigates to /auth/login → Azure
};

/**
 * Handles backend logout and clears session.
 *
 * This method sends a request to sign out the user in the back-end, and if successful, it will sign out the user in the front-end
 */
export const handleLogout = (options = {}) => {
	const { immediateRedirect = false } = options;
	const dispatch = store.dispatch;

	// Mark logout before clearing state so in-flight probes do not show the session-expired dialog.
	localStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "1");

	// Clear app state FIRST (synchronous)
	dispatch(clearSession());
	dispatch(resetConfigs()); // Reset debug/UI configs on logout
	sessionStorage.clear();
	debugLog(2, "[sessionUtils][FLOW] handleLogout session/configs cleared", {});

	const finalizeLogoutRedirect = () => {
		// Always complete IdP logout, even if backend logout failed
		dispatch(clearSession()); // idempotent safety
		sessionStorage.clear();

		// Explicitly remove MSAL accounts
		msalInstance.getAllAccounts().forEach((acc) => {
			msalInstance.removeAccount(acc);
		});

		// Add a logout marker once before redirect
		localStorage.setItem("__loggedOut", "1");

		msalInstance.logoutRedirect({ postLogoutRedirectUri: "/" });
	};

	if (immediateRedirect) {
		// Forced-expiry UX path: redirect now instead of waiting on backend round-trip.
		callApiToLogout().catch((error) => {
			errorLog(2, "[sessionUtils][FLOW] handleLogout failed (immediateRedirect mode)", {
				error: error.message,
				stack: error?.stack,
				raw: error,
			});
		});
		finalizeLogoutRedirect();
		return;
	}

	callApiToLogout()
		.then((data) => {
			if (!data || data.message !== "success") {
				errorLog(2, "[sessionUtils][FLOW] handleLogout unexpected backend response", {
					response: data,
				});
			}
		})
		.catch((error) => {
			errorLog(2, "[sessionUtils][FLOW] handleLogout failed", {
				error: error.message,
				stack: error?.stack,
				raw: error,
			});
		})
		.finally(() => {
			finalizeLogoutRedirect();
		});
};

/**
 * Get an Azure AD access token for MS Graph requests.
 * @param {Object} request - MSAL request options.
 * @returns {Promise<string|undefined>} The access token.
 */
export const getAccessToken = async (request) => {
	const msalInstance = new PublicClientApplication(msalConfig);

	try {
		const accounts = msalInstance.getAllAccounts();
		if (accounts.length > 0) {
			// const response = await msalInstance.loginPopup(loginRequest);
			const response = await msalInstance.acquireTokenSilent({
				...request,
				account: accounts[0],
			});
			return response.accessToken;
		} else {
			throw new Error("No accounts found.");
		}
	} catch (error) {
		errorLog(2, "[sessionUtils][FLOW] getAccessToken failed", { error: error.message });
	}
};
